import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Play, ShieldAlert, BadgeCheck, ImageIcon, Quote } from "lucide-react";
import { Counter, FadeUp } from "./Motion";
import { assetUrl, posterFor } from "../../lib/api";

function Evidence({ cs }) {
  const videos = Array.isArray(cs?.videos) ? cs.videos.filter(Boolean) : [];
  const shots = Array.isArray(cs?.screenshots) ? cs.screenshots.filter(Boolean) : [];
  const quote = cs?.testimonial?.quote;
  return (
    <div className="space-y-6 pt-6">
      <p className="text-base leading-relaxed text-slate-300">{cs.description}</p>
      {cs.revenue_note && (
        <p className="flex gap-2 rounded-xl border border-amber-300/20 bg-amber-300/[0.05] p-3 text-xs leading-relaxed text-amber-100/90">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" /> {cs.revenue_note}
        </p>
      )}
      <div>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><Play className="h-4 w-4 text-cyan-300" /> {videos.length > 1 ? "Watch the Ads We Ran →" : "Watch the Ad We Ran →"}</p>
        {videos.length ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {videos.map((v, i) => (
              <video key={v} controls preload="none" playsInline poster={posterFor(v)} aria-label={`${cs.business} ad ${i + 1}`}
                className="aspect-[9/16] w-full rounded-2xl border border-white/10 bg-black object-cover" data-testid={`case-video-${cs.id}-${i}`}>
                <source src={assetUrl(v)} type="video/mp4" />
              </video>
            ))}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-white/15 p-4 text-xs text-slate-400" data-testid={`case-video-pending-${cs.id}`}>Ad video coming soon.</p>
        )}
      </div>
      <div>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><ImageIcon className="h-4 w-4 text-cyan-300" /> Campaign screenshots</p>
        {shots.length ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {shots.map((s) => (
              <a key={s} href={assetUrl(s)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl border border-white/10 bg-[#1c2430]" data-testid={`case-screenshot-${cs.id}`}>
                <img src={assetUrl(s)} alt={`${cs.business} Meta Ads Manager results screenshot`} loading="lazy" className="aspect-square w-full object-contain transition-transform duration-500 hover:scale-105" />
              </a>
            ))}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-white/15 p-4 text-xs text-slate-400">Campaign screenshots will be added once verified.</p>
        )}
      </div>
      {quote && (
        <blockquote className="rounded-2xl glass p-5">
          <Quote className="h-5 w-5 text-cyan-300" />
          <p className="mt-3 font-serif text-xl italic text-white">“{quote}”</p>
          {cs.testimonial.author && <footer className="mt-3 text-sm text-slate-400">— {cs.testimonial.author}</footer>}
        </blockquote>
      )}
    </div>
  );
}

export function CaseCard({ cs, index, expandable = true }) {
  const [open, setOpen] = useState(false);
  const stats = Array.isArray(cs?.stats) ? cs.stats : [];
  return (
    <FadeUp delay={index * 0.12} className="group relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-panel" data-testid={`case-card-${cs.id}`}>
      <div className="relative h-80 overflow-hidden sm:h-[26rem]">
        <img src={cs.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl" />
        <img src={cs.image} alt={`${cs.business} ad frame`} loading="lazy" className="relative mx-auto h-full w-auto object-contain transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]" data-testid={`case-image-${cs.id}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-panel via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(0,136,255,0.35),transparent_55%)]" />
        <div className="absolute left-5 top-5 flex flex-wrap gap-2">
          <span className="rounded-full bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white backdrop-blur">Case Study {String(index + 1).padStart(2, "0")}</span>
          <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-medium backdrop-blur ${cs.figures_confirmed ? "bg-emerald-500/20 text-emerald-200" : "bg-ink/70 text-amber-200"}`}>
            {cs.figures_confirmed ? <><BadgeCheck className="h-3 w-3" /> Figures confirmed by client</> : "Client-reported figures"}
          </span>
        </div>
      </div>
      <div className="relative -mt-6 p-6 sm:p-8">
        {cs.logo && (
          <span className="mb-4 block h-16 w-16 overflow-hidden rounded-full bg-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] ring-4 ring-panel sm:h-20 sm:w-20" data-testid={`case-logo-${cs.id}`}>
            <img src={assetUrl(cs.logo)} alt={`${cs.business} logo`} loading="lazy" className="h-full w-full scale-[1.08] object-cover" />
          </span>
        )}
        <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">{cs.business}</h3>
        <p className="mt-1 text-sm text-slate-400">{[cs.location, cs.owner && `Owner: ${cs.owner}`, cs.campaign_start && `Started ${cs.campaign_start}`].filter(Boolean).join(" · ")}</p>
        <dl className="mt-7 grid grid-cols-3 gap-3 border-t border-white/[0.07] pt-6">
          {stats.map((s, i) => (
            <div key={i}>
              <dd className="text-xl sm:text-3xl font-semibold tracking-tight text-white">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals || 0} testId={`case-${cs.id}-stat-${i}`} />
              </dd>
              <dt className="mt-1.5 text-[11px] sm:text-xs leading-snug text-slate-400">{s.label}</dt>
            </div>
          ))}
        </dl>
        {expandable && (
          <>
            <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`case-toggle-${cs.id}`}
              className="mt-7 flex w-full items-center justify-between rounded-full border border-white/10 px-5 py-3 text-sm font-medium text-white transition-colors duration-300 hover:border-electric/60 hover:bg-electric/10">
              {open ? "Hide case study" : "Read the full case study"}
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                  <Evidence cs={cs} />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </FadeUp>
  );
}

export function ResultsDisclaimer({ text }) {
  return (
    <p className="mt-8 text-xs leading-relaxed text-slate-400" data-testid="results-disclaimer">
      {text || "Individual client results vary. These examples do not guarantee similar results."} Revenue figures are client-reported gross revenue, not profit, and do not account for the client's costs.
    </p>
  );
}
